import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Modal, Table, DatePicker, Button as AntdButton, Select } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import Cards from '../../components/Cards'
import { useGetAuditLogsQuery, useLazyGetAuditLogDetailQuery } from '../../services/auditLogSlice'
import { dateFormater } from '../../helper'
import InputField from '../../components/InputField'
import { useForm } from 'react-hook-form'
import { SearchIcon } from '../../components/Icons/iconImages'
import { Button } from '../../components/Buttons'
import { toast } from '../../components/Toast/toast'
import dayjs, { Dayjs } from 'dayjs'
import { useWorkspace } from '../../contexts/WorkspaceContext'

const { RangePicker } = DatePicker

interface AuditLogData {
  _id: string
  user_email: string
  action: string
  resource_type: string
  resource_id: string | null
  timestamp: string
  details?: any
  ip_address?: string
  user_agent?: string
  status?: string
  workspace_id?: string | null
  created_at?: string
}

function AuditLog() {
  const { currentWorkspace } = useWorkspace()

  // State for filters and pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [searchTerm, setSearchTerm] = useState('')
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null])
  const [statusFilter, setStatusFilter] = useState('all')

  // Form control for InputField
  const { control, watch } = useForm({
    defaultValues: {
      search: ''
    }
  })

  // Watch the search field and debounce the value
  const searchValue = watch('search')
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer for debounce
    debounceTimerRef.current = setTimeout(() => {
      setSearchTerm(searchValue || '')
      setPage(1) // Reset to first page on search
    }, 500)

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [searchValue])

  // Prepare query params
  const queryParams = useMemo(() => {
    const params: any = {
      page,
      page_size: pageSize,
      status: statusFilter === 'all' ? '' : statusFilter, // Always include status
      search: searchTerm,
    }

    if (dateRange[0] && dateRange[1]) {
      params.start_date = dateRange[0].startOf('day').toISOString()
      params.end_date = dateRange[1].endOf('day').toISOString()
    }

    if (currentWorkspace) {
      params.workspace_id = currentWorkspace
    }

    return params
  }, [page, pageSize, dateRange, currentWorkspace, statusFilter, searchTerm])

  const { data: auditLogData, isLoading, error, refetch } = useGetAuditLogsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  })

  const [getAuditLogDetail, { isLoading: isLoadingDetail }] = useLazyGetAuditLogDetailQuery()

  // Modal for detail view
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [selectedLog, setSelectedLog] = useState<any>(null)

  const handleViewDetail = async (logId: string) => {
    try {
      const response = await getAuditLogDetail({ id: logId }).unwrap()
      // Extract log from response object
      setSelectedLog(response?.log || response)
      setIsDetailModalOpen(true)
    } catch (error: any) {
      toast.error(error?.data?.error || 'Failed to load audit log detail')
      console.error('Error loading audit log detail:', error)
    }
  }

  const columns: ColumnsType<AuditLogData> = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: '15%',
      render: (timestamp: string) => (timestamp ? dateFormater(timestamp) : '-'),
      sorter: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    },
    {
      title: 'User Email',
      dataIndex: 'user_email',
      key: 'user_email',
      width: '18%',
      ellipsis: true,
    },
      {
      title: 'Workspace',
      dataIndex: 'workspace_name',
      key: 'workspace_name',
      width: '12%',
      ellipsis: true,
      render: (workspaceName: string) => workspaceName || '-',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: '10%',
      render: (action: string) => {
        if (!action) return '-'
        return action
          .replace(/_/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase())
      },
    },
    {
      title: 'Resource Type',
      dataIndex: 'resource_type',
      key: 'resource_type',
      width: '12%',
    },
  
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '8%',
      render: (status: string) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status === 'success' ? 'bg-green-100 text-green-700' :
            status === 'failure' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
          }`}>
          {status || '-'}
        </span>
      ),
    },
    // {
    //   title: 'IP Address',
    //   dataIndex: 'ip_address',
    //   key: 'ip_address',
    //   width: '10%',
    // },
    {
      key: 'actions',
      width: '8%',
      fixed: 'right',
      render: (_: any, record: AuditLogData) => (
        <Button
          type="text"
          size="small"
          onClick={() => handleViewDetail(record._id)}
        >
          View
        </Button>
      ),
    },
  ]

  // Map data with keys (search is handled by backend)
  const filteredData = useMemo(() => {
    const logs = auditLogData?.logs || []

    return logs.map((log: AuditLogData, index: number) => ({
      ...log,
      key: log._id || index,
    }))
  }, [auditLogData?.logs])

  // Use API response for pagination info
  const paginationInfo = {
    current: auditLogData?.page || page,
    pageSize: auditLogData?.page_size || pageSize,
    total: auditLogData?.total || 0,
    totalPages: auditLogData?.total_pages || 1
  }

  const handleDateRangeChange = (dates: any) => {
    setDateRange(dates || [null, null])
    setPage(1) // Reset to first page on filter change
  }

  const handleStatusChange = (status: any) => {
    setStatusFilter(status)
    setPage(1) // Reset to first page on filter change
  }

  const handleClearFilters = () => {
    setDateRange([null, null])
    setSearchTerm('')
    setStatusFilter('all')
    setPage(1)
  }

  return (
    <>
      <Cards variant='secondary' classname='p-0 sm:p-4 md:p-6' childClassName={'flex-col'} title={''} bottomButton={undefined}>
        <div className="flex w-full justify-between items-start border-b border-solid border-gray-300 pb-3 sm:pb-4">
          <div>
            <h4 className="text-[#181D27] font-semibold text-base sm:text-lg">Audit Logs</h4>
            <h5 className="font-normal text-[#535862] text-xs sm:text-sm">
              View system activity and user actions across all workspaces.
            </h5>
          </div>
        </div>

        {/* Filter and Search Controls */}
        <div className="flex flex-col gap-3 px-0 md:px-4 py-2 md:py-3">
          <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-3">
            {/* Search and Status Filter */}
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:flex-1">
              <div className="w-full sm:flex-1">
                <InputField
                  control={control}
                  name="search"
                  placeholder="Search by Email, Action, Resource..."
                  icon={<SearchIcon className="text-gray-500 w-4 h-4" />}
                  iconDirection="left"
                  className="w-full"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={handleStatusChange}
                className="w-full sm:w-[180px]"
                placeholder="Filter by Status"
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'success', label: 'Success' },
                  { value: 'failure', label: 'Failure' },
                ]}
              />
            </div>

            {/* Date Range and Clear Filters */}
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                className="w-full sm:w-auto"
                placeholder={['Start Date', 'End Date']}
              />
              <AntdButton onClick={handleClearFilters} className="w-full sm:w-auto">
                Clear Filters
              </AntdButton>
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
            total: paginationInfo.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            responsive: true,
            size: 'small',
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (newPage, newPageSize) => {
              setPage(newPage)
              if (newPageSize !== pageSize) {
                setPageSize(newPageSize || 20)
                setPage(1) // Reset to first page when changing page size
              }
            },
          }}
          scroll={{ x: 'max-content' }}
          className="responsive-table"
        />
      </Cards>

      {/* Detail Modal */}
      <Modal

        open={isDetailModalOpen}
        onCancel={() => {
          setIsDetailModalOpen(false)
          setSelectedLog(null)
        }}
        footer={
          <div className="flex justify-end gap-2 pt-3 pb-2 px-4">
            <Button
              key="close"
              onClick={() => {
                setIsDetailModalOpen(false)
                setSelectedLog(null)
              }}
            >
              Close
            </Button>
          </div>
        }
        width={700}
        centered
        styles={{
          body: { padding: '24px' }
        }}
      >
        {isLoadingDetail ? (
          <div className="flex justify-center items-center py-8">
            <p>Loading...</p>
          </div>
        ) : selectedLog ? (
          <div className="space-y-6">

            <div className="pb-2">
              <h4 className="text-lg font-semibold">Audit Log Detail</h4>
              {selectedLog && (
                <p className="text-sm text-gray-500 mt-1">
                  Log ID: {selectedLog._id}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-500">User Name</p>
                <p className="text-sm">{selectedLog.user_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">User Email</p>
                <p className="text-sm">{selectedLog.user_email}</p>
              </div>
                <div>
                <p className="text-sm font-semibold text-gray-500">Workspace Name</p>
                <p className="text-sm">{selectedLog.workspace_name || '-'}</p>
              </div>
               <div>
                <p className="text-sm font-semibold text-gray-500">Resource Type</p>
                <p className="text-sm">{selectedLog.resource_type}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Activity</p>
                <p className="text-sm">{selectedLog.activity || '-'}</p>
              </div>
             
            
              <div>
                <p className="text-sm font-semibold text-gray-500">Status</p>
                <p className="text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedLog.status === 'success' ? 'bg-green-100 text-green-700' :
                      selectedLog.status === 'failure' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                    }`}>
                    {selectedLog.status || '-'}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Timestamp</p>
                <p className="text-sm">{selectedLog.timestamp ? dateFormater(selectedLog.timestamp) : '-'}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">IP Address</p>
                <p className="text-sm">{selectedLog.ip_address || '-'}</p>
              </div>
            </div>
            {selectedLog.query && (
              <div className="pt-2">
                <p className="text-sm font-semibold text-gray-500 mb-1">Query</p>
                <pre className="text-xs bg-gray-50 p-4 rounded border border-gray-200 overflow-auto max-h-32">
                  {selectedLog.query}
                </pre>
              </div>
            )}
            {selectedLog.user_agent && (
              <div className="pt-2">
                <p className="text-sm font-semibold text-gray-500 mb-1">User Agent</p>
                <p className="text-sm break-all">{selectedLog.user_agent}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center py-8">No data available</p>
        )}
      </Modal>
    </>
  )
}

export default AuditLog
